import React, { useState } from "react";
import { useErrorTopicOptions } from "../../../../hooks/useErrorTopic";
import dayjs from "dayjs";
import {
  Modal,
  Descriptions,
  Typography,
  Button,
  message,
  Input,
  Select,
  Checkbox,
  InputNumber,
} from "antd";
import CaseDetailsModal from "../../../../components/CaseDetailsModal";
import {
  useFetchPassImportInputAudit,
  useFetchFailImportInputAudit,
} from "../hooks/useFetch";

const { Title } = Typography;

const formatAuditDate = (d) => dayjs(d).format("DD/MM/YYYY");
const { TextArea } = Input;

const DetailsModal = ({
  visible,
  onClose,
  record,
  refetch,
  setDetailsModalVisible,
}) => {
  const [passModalVisible, setPassModalVisible] = useState(false);
  const [failModalVisible, setFailModalVisible] = useState(false);
  const [remark, setRemark] = useState("");
  const [description, setDescription] = useState("");
  // "ยอดผิด" — ให้คนตรวจระบุความเสียหายจริงเอง (ไม่ใช่ยอดของเคส)
  // เช่น เคสยอด 1,000 แต่เติมเกินไป 100 → ความเสียหาย = 100
  const [isWrongAmount, setIsWrongAmount] = useState(false);
  const [wrongAmount, setWrongAmount] = useState(null);
  const { mutateAsync: passImportInputAudit } = useFetchPassImportInputAudit();
  const { mutateAsync: failImportInputAudit } = useFetchFailImportInputAudit();
  // หัวข้อเคสผิดพลาด มาจาก master data (admin > Master Data > Error Topic)
  const { options: remarkOptions } = useErrorTopicOptions();

  if (!record) return null;

  const handlePass = () => {
    setPassModalVisible(true);
  };

  const handlePassConfirm = () => {
    passImportInputAudit({ id: record.id }).then(() => {
      setPassModalVisible(false);
      setDetailsModalVisible(false);
      refetch();
      message.success("Save Success");
    });
  };

  const handleFail = () => {
    // ถ้าตรวจ Fail ไปแล้วแล้วกดเข้ามาแก้ → เติมค่าเดิมให้ ไม่ต้องกรอกใหม่ทั้งชุด
    // (ยอดความเสียหายเดิมมาจาก a.wrong_amount ที่ /list ส่งมาเป็น wrongAmount)
    const isEditingFail = record.auditStatus === "Fail";
    setRemark(isEditingFail ? record.failRemark || "" : "");
    setDescription(isEditingFail ? record.auditDescription || "" : "");
    setIsWrongAmount(isEditingFail ? record.wrongAmount != null : false);
    setWrongAmount(isEditingFail ? record.wrongAmount : null);
    setFailModalVisible(true);
  };

  const handleFailConfirm = () => {
    if (!remark.trim()) {
      message.error("กรุณากรอก Remark ก่อน");
      return;
    }
    // ติ๊กว่ายอดผิดแล้วต้องกรอกยอด ไม่งั้นหน้าสรุปจะได้ค่าว่างแล้วนับเป็น 0 เงียบ ๆ
    if (isWrongAmount && (wrongAmount == null || wrongAmount === "")) {
      message.error("กรุณากรอกยอดความเสียหาย");
      return;
    }
    setFailModalVisible(false);
    failImportInputAudit({
      id: record.id,
      remark: remark,
      description: description,
      wrongAmount: isWrongAmount ? wrongAmount : null,
    }).then(() => {
      setFailModalVisible(false);
      setDetailsModalVisible(false);
      refetch();
      message.success("Save Success");
      setRemark("");
      setDescription("");
      setIsWrongAmount(false);
      setWrongAmount(null);
    });
  };

  // ข้อมูลจากชีตที่ตรวจ — ข้อมูลของเคสจริงแสดงจาก modal ตัวกลาง (ดึงสดตามเลขเคส)
  const auditInfo = (
    <>
      <Title level={4}>ข้อมูลจากชีต (Audit)</Title>
      <Descriptions bordered size="small" column={2} style={{ marginBottom: 24 }}>
        <Descriptions.Item label="User (ชีต)">{record.auditUserId || "-"}</Descriptions.Item>
        <Descriptions.Item label="ยอด (ชีต)">{record.auditAmount ?? "-"}</Descriptions.Item>
        <Descriptions.Item label="วันที่ (ชีต)">{record.auditDate ? formatAuditDate(record.auditDate) : "-"}</Descriptions.Item>
        <Descriptions.Item label="จับคู่เคส">{record.matchStatus || "-"}</Descriptions.Item>
        <Descriptions.Item label="ตรวจซ้ำ">{record.dupStatus || "-"}</Descriptions.Item>
        <Descriptions.Item label="ผลตรวจ">{record.auditStatus || "-"}</Descriptions.Item>
        {record.auditStatus === "Fail" && (
          <Descriptions.Item label="เหตุผลที่ Fail" span={2}>
            <div style={{ whiteSpace: "pre-wrap", wordBreak: "break-word" }}>{record.failRemark || "-"}</div>
          </Descriptions.Item>
        )}
        {record.wrongAmount != null && (
          <Descriptions.Item label="ยอดความเสียหาย">{record.wrongAmount}</Descriptions.Item>
        )}
      </Descriptions>
    </>
  );

  return (
    <>
      <CaseDetailsModal
        open={visible}
        onClose={onClose}
        // record.id ของหน้านี้คือเลขแถว audit (ใช้กับปุ่ม Pass/Fail) — เลขเคสอยู่ที่ inputId
        caseId={record.inputId}
        extra={auditInfo}
        footer={
        // ตรวจ Fail ไปแล้วต้องแก้กลับเป็น Pass ได้ (เดิมซ่อนปุ่มทั้งแถบ = แก้ไม่ได้เลย
        // ต้องไปแก้ที่ฐานข้อมูลอย่างเดียว) — โชว์เฉพาะปุ่มแก้เป็น Pass ไม่ต้องมีปุ่ม Fail ซ้ำ
        record.auditStatus === "Fail" ? (
          <div style={{ textAlign: "right" }}>
            <Button
              style={{
                padding: "20px 40px",
                marginRight: 16,
                fontSize: 16,
              }}
              onClick={handleFail}
            >
              แก้ยอด/เหตุผล
            </Button>
            <Button
              type="primary"
              style={{
                color: "#fff",
                padding: "20px 40px",
                fontSize: 16,
              }}
              onClick={handlePass}
            >
              แก้เป็น Pass
            </Button>
          </div>
        ) : (
          <div style={{ textAlign: "right" }}>
            <Button
              type="primary"
              style={{
                color: "#fff",
                padding: "20px 40px",
                marginRight: 16,
                fontSize: 16,
              }}
              onClick={handlePass}
            >
              Pass
            </Button>
            <Button
              type="primary"
              danger
              style={{
                color: "#fff",
                padding: "20px 40px",
                fontSize: 16,
              }}
              onClick={handleFail}
            >
              Fail
            </Button>
          </div>
        )
        }
      />

      <Modal
        title={record.auditStatus === "Fail" ? "ยืนยันการแก้เป็น Pass" : "ยืนยันการ Pass"}
        open={passModalVisible}
        onOk={handlePassConfirm}
        onCancel={() => setPassModalVisible(false)}
        okText="ตกลง"
        cancelText="ยกเลิก"
      >
        {record.auditStatus === "Fail" ? (
          <>
            <p>คุณต้องการแก้รายการนี้จาก Fail เป็น Pass ใช่หรือไม่?</p>
            <p style={{ color: "#d4380d", marginBottom: 0 }}>
              เหตุผลที่ตรวจไม่ผ่าน (Fail Remark) และยอดความเสียหาย จะถูกล้างทิ้ง
              และรายการนี้จะไม่ถูกนับเป็นเคสผิดพลาดใน KPI อีกต่อไป
            </p>
          </>
        ) : (
          <p>คุณต้องการยืนยันการ Pass ใช่หรือไม่?</p>
        )}
      </Modal>

      <Modal
        title={record.auditStatus === "Fail" ? "แก้รายละเอียด Fail" : "ยืนยันการ Fail"}
        open={failModalVisible}
        onOk={handleFailConfirm}
        onCancel={() => {
          setFailModalVisible(false);
          setRemark("");
          setDescription("");
          setIsWrongAmount(false);
          setWrongAmount(null);
        }}
        okText="ตกลง"
        cancelText="ยกเลิก"
      >
        <div>
          <p style={{ marginBottom: 8 }}>
            {record.auditStatus === "Fail"
              ? "แก้เหตุผล / ยอดความเสียหาย ของรายการที่ตรวจไม่ผ่านนี้"
              : "คุณต้องการยืนยันการ Fail ใช่หรือไม่?"}
          </p>
          <Select
            style={{ width: "100%" }}
            options={remarkOptions}
            value={remark}
            onChange={(value) => setRemark(value)}
            showSearch
          />
          <TextArea
            placeholder="ระบุรายละเอียด"
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            style={{ marginTop: 8 }}
            rows={4}
          />

          <div style={{ marginTop: 12 }}>
            <Checkbox
              checked={isWrongAmount}
              onChange={(e) => {
                setIsWrongAmount(e.target.checked);
                if (!e.target.checked) setWrongAmount(null);
              }}
            >
              ยอดผิด
            </Checkbox>
            {isWrongAmount && (
              <div style={{ marginTop: 8 }}>
                <InputNumber
                  style={{ width: "100%" }}
                  min={0}
                  step={1}
                  placeholder="ยอดความเสียหายจริง (บาท)"
                  value={wrongAmount}
                  onChange={setWrongAmount}
                />
                <div style={{ fontSize: 12, color: "#8c8c8c", marginTop: 4 }}>
                  กรอก<strong>ยอดที่ผิดพลาดจริง</strong> ไม่ใช่ยอดของเคส —
                  เช่น เคส 1,000 แต่เติมเกินไป 100 ให้กรอก 100
                </div>
              </div>
            )}
          </div>
        </div>
      </Modal>
    </>
  );
};

export default DetailsModal;
